import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
  CtyButton,
  CtySpinner,
  pdfViewPayload,
  sharedPDFViewHandler,
  SourceElementType,
  ArticleLayout,
  useAgGridStatePersistence,
  useTheme,
} from '@circuitry-ai/doc-ui';
import { getExplainerGridStyle } from './explainerGridStyle';
import {
  RootState,
  useGetPdfLinkForConversationsMutation,
} from '@circuitry-ai/doc-data';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'github-markdown-css/github-markdown.css';
import { ColDef, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';

export enum ExplainAnswerTableFields {
  CONTEXT = 'page_content',
  PAGE_LABEL = 'page_labels',
  SOURCE_NAME = 'file_name',
  CHUNK_ID = 'chunk_id',
}

const explainerFormatScoreCell = (params: { value?: number | string | null }) => {
  const v = params?.value;
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'number' && !Number.isNaN(v)) return v.toFixed(2);
  return String(v);
};

const ExplainerCompactDescriptionCell = (params: ICellRendererParams) => {
  const raw = params.value;
  if (raw == null || raw === '') return null;
  const text = String(raw).replace(/\n{2,}/g, '\n');
  return (
    <div className="max-w-full whitespace-pre-wrap break-words text-[13px] leading-tight [&_p]:my-0">
      {text}
    </div>
  );
};

const ExplainerImageCell = (params: {
  data?: { presigned_url?: string; _urlsResolved?: boolean };
}) => {
  const url = params.data?.presigned_url;
  const resolved = params.data?._urlsResolved;
  if (!resolved) {
    return (
      <div className="flex min-h-[80px] items-center justify-center py-2">
        <CtySpinner size="sm" />
      </div>
    );
  }
  if (!url) {
    return <span className="text-default-400">—</span>;
  }
  return (
    <div className="flex items-center justify-center py-2">
      <img
        src={url}
        alt=""
        className="max-h-40 max-w-full rounded border border-default-200 object-contain"
      />
    </div>
  );
};

export type ExplainerTableTab = 'knowledge' | 'ranking' | 'images';

const ExplainerTable = ({
  rowData,
  advisor_id,
  tab,
}: {
  rowData?: any[];
  advisor_id?: string;
  tab?: ExplainerTableTab;
}) => {
  const { isDark } = useTheme();
  const gridStyle = useMemo(() => getExplainerGridStyle(isDark), [isDark]);
  const [pdfLoadingIndex, setPdfLoadingIndex] = useState<string>('');
  const [articleSourceId, setArticleSourceId] = useState<string | null>(null);
  const [isArticleReadOnly, setIsArticleReadOnly] = useState(false);
  const [getPdfLink] = useGetPdfLinkForConversationsMutation();
  const { activeChatAdvisor } = useSelector(
    (state: RootState) => state.advisor
  );

  const paginationPageSizeSelector = useMemo(() => {
    return [10, 20, 40, 50];
  }, []);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
    }),
    []
  );

  const { augmentGridReady, mergeColumnDefs, suppressDragLeaveHidesColumns } = useAgGridStatePersistence('explainer-table');

  const onGridReady = useCallback((params: GridReadyEvent) => {
    augmentGridReady(params, (p) => {
      p.api.sizeColumnsToFit?.();
    });
  }, [augmentGridReady]);

  const renderFileNameCell = (params: any) => {
    const source = params.data;
    if (tab === 'images' && !source?.source_id) {
      return <div className="px-1 text-left text-sm">{params.value ?? ''}</div>;
    }
    const isFileNameLoading =
      pdfLoadingIndex ===
      source['source_id'] +
      (source?.['page_labels']?.length > 0 ? source?.['page_labels'][0] : '') +
      '' +
      params.rowIndex +
      params.column.colId;
    return (
      <CtyButton
        id={`sourcename-link-${params.value}`}
        color="white"
        className="flex cursor-pointer justify-start text-left"
        onPress={() => onFileNameClick(params)}
        title={params?.value}
      >
        <span>{isFileNameLoading && <CtySpinner size="sm" />}</span>
        <div>{params?.value}</div>
      </CtyButton>
    );
  };

  const renderPageNumberCell = (params: any) => {
    const source = params.data;
    const isFileNameLoading =
      pdfLoadingIndex ===
      source['source_id'] +
      (source?.['page_labels']?.length > 0 ? source?.['page_labels'][0] : '') +
      '' +
      params.rowIndex +
      params.column.colId;
    if (source?.element_refs?.[0]?.file_type === 'pdf') {
      return (
        <CtyButton
          id={`sourcename-link-${params.value}`}
          color="white"
          className="flex cursor-pointer justify-start text-left"
          onPress={() => onFileNameClick(params)}
        >
          <span>{isFileNameLoading && <CtySpinner size="sm" />}</span>
          <div>{params?.value}</div>
        </CtyButton>
      );
    }
  };

  const onFileNameClick = (item: any) => {
    const source = item?.data;
    if (
      [
        SourceElementType.YOUTUBE,
        SourceElementType.URL,
        SourceElementType.API,
      ].includes(source?.element_refs?.[0]?.file_type)
    ) {
      window.open(source?.element_refs?.[0]?.url, '_blank');
      return;
    }
    
    // Handle expert_answer and knowledge_article by showing article modal
    if (source?.element_type === SourceElementType.EXPERT_ANSWER || source?.element_type === SourceElementType.KNOWLEDGE_ARTICLE) {
      setIsArticleReadOnly(true);
      setArticleSourceId(source?.source_id);
      return;
    }
    
    // Check if this is a ditamap file and use sub_source_id instead
    const isDitaMap = source?.element_refs?.[0]?.file_type === 'ditamap';
    const sourceIdToUse = isDitaMap && source?.element_refs?.[0]?.sub_source_id
      ? source.element_refs[0].sub_source_id
      : source['source_id'];

    const pageLabels = Array.isArray(source?.page_labels)
      ? source.page_labels.map(Number).filter((n: number) => !Number.isNaN(n))
      : [];
    const apiPayLoad: pdfViewPayload = {
      advisor_id: advisor_id ?? activeChatAdvisor?.advisor_id,
      source_ids: [
        {
          source_id: sourceIdToUse,
          page_labels: pageLabels,
        },
      ],
    }
    const loadingState =
      sourceIdToUse +
      (source?.['page_labels']?.length > 0 ? source?.['page_labels'][0] : '') +
      '' +
      item?.rowIndex +
      item.column.colId;
    sharedPDFViewHandler(
      loadingState,
      setPdfLoadingIndex,
      apiPayLoad,
      getPdfLink
    );
  };

  const columnDefs: ColDef[] = useMemo(() => {
    const rowNumberCol: ColDef = {
      headerName: '#',
      field: 'rowNumber',
      sortable: false,
      cellStyle: { fontSize: '14px', color: 'inherit', textAlign: 'center' },
      minWidth: 60,
      maxWidth: 80,
      cellRenderer: (params: any) => params.rowIndex + 1,
      suppressSizeToFit: true,
    };

    if (tab === 'images') {
      return [
        rowNumberCol,
        {
          headerName: 'Image',
          field: 'presigned_url',
          sortable: false,
          cellRenderer: ExplainerImageCell,
          minWidth: 300,
          autoHeight: true,
          wrapText: true,
          suppressSizeToFit: true,
        },
        {
          headerName: 'Image Description',
          field: 'image_description',
          sortable: false,
          cellStyle: {
            fontSize: '13px',
            color: 'inherit',
            textAlign: 'left',
            lineHeight: '1.25',
            paddingTop: '4px',
            paddingBottom: '4px',
          },
          minWidth: 280,
          wrapText: true,
          autoHeight: true,
          cellRenderer: ExplainerCompactDescriptionCell,
          filter: 'agTextColumnFilter',
          filterParams: {
            filterOptions: ['contains'],
            defaultOption: 'contains',
            debounceMs: 200,
          },
        },
        {
          headerName: 'Page Number',
          field: 'page_label',
          sortable: false,
          cellStyle: { fontSize: '14px', color: 'inherit', textAlign: 'left' },
          minWidth: 120,
        },
        {
          headerName: 'Source name',
          field: ExplainAnswerTableFields.SOURCE_NAME,
          colId: 'file_name',
          sortable: false,
          cellStyle: {
            color: '#2E90FA',
            fontSize: '14px',
            textDecoration: 'underline',
            cursor: 'pointer',
          },
          minWidth: 200,
          flex: 1,
          cellRenderer: renderFileNameCell,
        },
        {
          headerName: 'Confidence Score',
          field: 'confidence_score',
          sortable: false,
          minWidth: 150,
          cellRenderer: explainerFormatScoreCell,
        },
        {
          headerName: 'Ranked Score',
          field: 'ranked_score',
          sortable: false,
          minWidth: 150,
          cellRenderer: explainerFormatScoreCell,
        },
      ] as ColDef[];
    }

    const baseColumns: ColDef[] = [
      rowNumberCol,
      {
        headerName: 'Context',
        field: ExplainAnswerTableFields.CONTEXT,
        sortable: false,
        cellStyle: { fontSize: '14px', color: 'inherit' },
        minWidth: 700,
        cellRenderer: AccordionCell,
        wrapText: true,
        autoHeight: true,
        suppressSizeToFit: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          debounceMs: 200,
        },
      },
      {
        headerName: 'Page number',
        field: ExplainAnswerTableFields.PAGE_LABEL,
        cellStyle: {
          color: '#2E90FA',
          fontSize: '14px',
          textDecoration: 'underline',
          cursor: 'pointer',
        },
        sortable: false,
        minWidth: 200,
        cellRenderer: renderPageNumberCell,
      },
      {
        headerName: 'Chunk ID',
        field: ExplainAnswerTableFields.CHUNK_ID,
        cellStyle: { fontSize: '14px', color: 'inherit' },
        minWidth: 200,
        sortable: false,
      },
      {
        headerName: 'Source name',
        field: ExplainAnswerTableFields.SOURCE_NAME,
        cellStyle: {
          color: '#2E90FA',
          fontSize: '14px',
          textDecoration: 'underline',
          cursor: 'pointer',
        },
        sortable: false,
        minWidth: 200,
        flex: 1,
        cellRenderer: renderFileNameCell,
      },
    ];

    const scoreColumn: ColDef =
      tab === 'ranking'
        ? {
            headerName: 'Ranked Score',
            field: 'ranked_score',
            cellStyle: { fontSize: '14px', color: 'inherit' },
            sortable: false,
            minWidth: 150,
            cellRenderer: (params: any) => {
              return params?.value?.toFixed(2);
            },
          }
        : {
            headerName: 'Confidence Score',
            field: 'confidence_score',
            cellStyle: { fontSize: '14px', color: 'inherit' },
            sortable: false,
            minWidth: 150,
            cellRenderer: (params: any) => {
              return params?.value?.toFixed(2);
            },
          };

    return [...baseColumns, scoreColumn];
  }, [tab, pdfLoadingIndex, advisor_id, activeChatAdvisor?.advisor_id]);

  const columnDefsWithSort = useMemo(() => mergeColumnDefs(columnDefs), [columnDefs, mergeColumnDefs]);

  return (
    <>
      <div className={'ag-theme-alpine mt-2'} style={gridStyle}>
        <AgGridReact
          suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
          rowData={rowData || []}
          columnDefs={columnDefsWithSort}
          defaultColDef={{
            ...defaultColDef,
            floatingFilter: true, // This adds the filter input below headers
          }}
          headerHeight={40}
          domLayout="autoHeight"
          onGridReady={onGridReady}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={paginationPageSizeSelector}
          alwaysShowHorizontalScroll={true}
        />
      </div>
      {articleSourceId && (
        <ArticleLayout
          articleId={articleSourceId}
          readOnly={isArticleReadOnly}
          conversationView
          onClose={() => {
            setArticleSourceId(null);
            setIsArticleReadOnly(false);
          }}
        />
      )}
    </>
  );
};

export default ExplainerTable;

const markdownComponents = {
  ul: ({ node, ...props }: any) => (
    <ul className="list-disc ml-4" {...props} />
  ),
  ol: ({ node, ...props }: any) => (
    <ol className="list-decimal ml-4" {...props} />
  ),
  p: (props: any) => (
    <p className="my-1" {...props} />
  ),
};

const AccordionCell = ({ value }: { value: string; }) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Add effect to reset scroll position when collapsing
  useEffect(() => {
    if (!isOpen && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  return (
    <button
      className="px-2 flex relative min-h-[24px] cursor-pointer w-full"
      onClick={() => setIsOpen(!isOpen)}
    >
      <button
        className="absolute left-2 top-[50%] translate-y-[-50%] transition-all duration-300 ease-in-out"
        style={{
          transform: `translateY(-50%) rotate(${isOpen ? '90deg' : '0deg'})`,
        }}
        title={isOpen ? 'Collapse' : 'Expand'}
      >
        <Icon
          icon="material-symbols:keyboard-arrow-right"
          width="24"
          height="24"
          className="text-black dark:text-[#f5f5f5]"
        />
      </button>
      <div
        ref={contentRef}
        className={`
          ml-6 
          transition-all 
          duration-300 
          ease-in-out
          ${isOpen ? 'max-h-[500px]' : 'max-h-[34px]'}
          ${isOpen ? 'overflow-y-auto' : 'overflow-hidden'}
          scroll-smooth
          w-full
          text-left
        `}
        style={{
          scrollBehavior: 'smooth'
        }}
      >
        <div className={`
          markdown-body
          ${!isOpen && 'whitespace-nowrap text-ellipsis overflow-hidden'}
        `}
          style={{
            color: 'black',
            backgroundColor: 'transparent',
          }}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {value}
          </ReactMarkdown>
        </div>
      </div>
    </button>
  );
};